import React, { useState, useMemo, useRef, useCallback } from 'react';
import CardDetail from './CardDetail.js';
import CardPreview from './CardPreview.js';
import CostCurveChart from './CostCurveChart.js';
import { saveToStorage} from '../utils/localStorage.js';
import InfoModal from './overlays/InfoModal.js';
import DeckBuilderHelpOverlay from './overlays/DeckBuilderHelpOverlay.js'; 
import DeckBuilderLibraryCard from './DeckBuilderLibraryCard.js';
import TutorialModal from './overlays/TutorialModal.js';
import TutorialHighlight from './TutorialHighlight.js';
import useDeckBuilderTutorial from '../hooks/useDeckBuilderTutorial.js';
import { MIN_DECK_SIZE, MAX_DECK_SIZE } from '../gameLogic/constants.js';
import { getCardName, getCardLoc, getUILoc } from '../utils/cardUtils.js';
import { CardIllustration } from './Card.js';
import './DeckBuilderScreen.css';
import './CostCurveChart.css';
import './overlays/InfoModal.css';
import audioManager from '../utils/AudioManager.js';

const DeckBuilderScreen = ({ gameData, onExit, deckToEdit }) => {
  const { cardDefs } = gameData;
  const [deck, setDeck] = useState([]);
  const [deckName, setDeckName] = useState('');
  const [allDecks, setAllDecks] = useState({});
  const [sortBy, setSortBy] = useState('implementation');
  const [filterType, setFilterType] = useState('all');
  const [filterScale, setFilterScale] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCard, setSelectedCard] = useState(null);
  const [costCurveMode, setCostCurveMode] = useState('single');
  const fileInputRef = useRef(null);
  const [modalInfo, setModalInfo] = useState({ isOpen: false, message: '' });
  const [isHelpVisible, setHelpVisible] = useState(false);
  // プレビューの表示状態と固定位置を管理
  const [hoveredCard, setHoveredCard] = useState(null);
  const [previewPos, setPreviewPos] = useState({ x: 0, y: 0 });
  const previewRef = useRef(null);
  
  // チュートリアルHookの導入
  const { 
    isActive: isTutorialActive, 
    tutorialData, 
    nextStep: nextTutorialStep,
    restartTutorial
  } = useDeckBuilderTutorial();

  // カードホバー時に位置を固定
  const handleMouseEnter = useCallback((card, e) => {
    // すでに同じカードなら再計算の必要なし
    if (hoveredCard?.id === card.id) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    
    // 計算した位置で状態をセット
    setHoveredCard(card);

    // 要素がDOMに反映されてサイズが取れるまで再帰的に計測
    const pollSize = () => {
        if (previewRef.current && previewRef.current.offsetHeight > 0) {
            const previewHeight = previewRef.current.offsetHeight;
            const previewWidth = previewRef.current.offsetWidth;
            
            const x = (rect.right + rect.left) / 2;
            const y = (rect.top + rect.bottom) / 2;
            
            const isOverflowRight = (x + previewWidth) > window.innerWidth;
            const isOverflowBottom = (y + previewHeight) > window.innerHeight;

            setPreviewPos({
                x: isOverflowRight ? Math.max(10, rect.left - previewWidth - 10) : x,
                y: isOverflowBottom ? Math.max(10, window.innerHeight - previewHeight - 10) : y
            });
        } else {
            requestAnimationFrame(pollSize);
        }
    };
    
    requestAnimationFrame(pollSize);
  }, [hoveredCard]);

  const handleCardMouseLeave = useCallback((e) => {
    setHoveredCard(null);
  }, []);

  const filteredAndSortedCards = useMemo(() => {
    if (!cardDefs) return [];
    let cards = Object.values(cardDefs);

    cards = cards.filter(card => !card.is_token);

    // 有効フラグフィルター
    cards = cards.filter(card => card.is_enabled !== false);

    if (filterType !== 'all') {
      const typeNum = parseInt(filterType);
      cards = cards.filter(card => card.card_type === typeNum);
    }

    if (filterScale !== 'all') {
      cards = cards.filter(card => {
        const scale = card.required_scale;
        switch (filterScale) {
          case '0-2':
            return scale >= 0 && scale <= 2;
          case '3-15':
            return scale >= 3 && scale <= 15;
          case '16+':
            return scale >= 16;
          default:
            return true;
        }
      });
    }

    // テキスト絞り込み（スペース区切りの複数キーワード対応）
    if (searchQuery.trim()) {
      const keywords = searchQuery.trim().toLowerCase().split(/[\s　]+/).filter(Boolean);
      if (keywords.length > 0) {
        cards = cards.filter(card => {
          const cardLoc = getCardLoc(card.id);
          const name = (cardLoc?.name || card.name || '').toLowerCase();
          const desc = (cardLoc?.description || card.description || '').toLowerCase();
          const fullText = `${name} ${desc}`;
          return keywords.every(kw => fullText.includes(kw));
        });
      }
    }

    cards.sort((a, b) => {
      if (sortBy === 'requiredScale') {
        return a.required_scale - b.required_scale;
      }
      return (a.definitionOrder || 0) - (b.definitionOrder || 0);
    });

    return cards;
  }, [cardDefs, sortBy, filterType, filterScale, searchQuery]);
  
  const costCurveData = useMemo(() => {
    const counts = {};
    if (costCurveMode === 'single') {
      deck.forEach(card => {
        const cost = card.required_scale;
        counts[cost] = (counts[cost] || 0) + 1;
      });
      const maxCost = Math.max(...Object.keys(counts).map(Number), 0);
      const data = [];
      for (let i = 0; i <= maxCost; i++) {
        if (counts[i] > 0) {
          data.push({ label: `${i}`, count: counts[i] });
        }
      }
      return data;
    } else {
      const groups = { '0': 0, '5': 0, '10': 0, '15': 0, '20+': 0 };
      deck.forEach(card => {
        const cost = card.required_scale;
        if (cost <= 4) groups['0']++;
        else if (cost <= 9) groups['5']++;
        else if (cost <= 14) groups['10']++;
        else if (cost <= 19) groups['15']++;
        else groups['20+']++;
      });
      return Object.entries(groups).map(([label, count]) => ({ label, count }));
    }
  }, [deck, costCurveMode]);

  const addCardToDeck = useCallback((card) => {
    if (isTutorialActive) return;

    if (deck.length >= MAX_DECK_SIZE) {
      audioManager.playSE('cancel');
      setModalInfo({ isOpen: true, message: getUILoc('MESSAGES.MAX_DECK_SIZE_ERROR', { max: MAX_DECK_SIZE }) });
      return;
    }
    audioManager.playSE('select');
    const newDeck = [...deck, card].sort((a, b) => a.required_scale - b.required_scale);
    setDeck(newDeck);
  }, [deck, isTutorialActive]);

  const removeCardFromDeck = useCallback((cardToRemove, index) => {
    if (isTutorialActive) return;

    audioManager.playSE('cancel');
    const newDeck = deck.filter((_, i) => i !== index);
    setDeck(newDeck);
  }, [deck, isTutorialActive]);

  const handleShowCardDetails = useCallback((card) => {
    if (isTutorialActive) return;

    audioManager.playSE('select');
    setSelectedCard(card);
  }, [isTutorialActive]);

  const handleSaveDeck = useCallback(() => {
    if (!deckName || deck.length === 0) {
      audioManager.playSE('cancel');
      setModalInfo({ isOpen: true, message: !deckName ? getUILoc('MESSAGES.ENTER_DECK_NAME') : getUILoc('MESSAGES.EMPTY_DECK_ERROR') });
      return;
    }
    audioManager.playSE('select');
    const newDecks = {
      ...allDecks,
      [deckName]: {
        name: deckName,
        cards: deck.map(c => c.id),
      }
    };
    saveToStorage('customDecks', newDecks);
    setAllDecks(newDecks);
    setModalInfo({ isOpen: true, message: getUILoc('MESSAGES.DECK_SAVED', { name: deckName }) });
  }, [deck, deckName, allDecks]);

  const handleExportDeck = useCallback(() => {
    if (!deckName || deck.length === 0) {
      audioManager.playSE('cancel');
      setModalInfo({ isOpen: true, message: getUILoc('MESSAGES.EXPORT_BEFORE_NAME_ERROR') });
      return;
    }
    audioManager.playSE('select');
    const deckData = {
      name: deckName,
      cards: deck.map(c => c.id),
    };
    const jsonString = JSON.stringify(deckData, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${deckName}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [deck, deckName]);

  const handleImportClick = () => {
    audioManager.playSE('select');
    fileInputRef.current.click();
  };

  const handleExit = () => {
    audioManager.playSE('cancel');
    onExit();
  };

  const handleImportDeck = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importedDeckData = JSON.parse(e.target.result);
        if (importedDeckData && importedDeckData.name && Array.isArray(importedDeckData.cards)) {
          if (allDecks[importedDeckData.name]) {
            if (!window.confirm(getUILoc('MESSAGES.DUPLICATE_DECK_CONFIRM', { name: importedDeckData.name }))) {
              event.target.value = '';
              return;
            }
          }

          const newDeckCards = importedDeckData.cards.map(cardIdOrName => {
            return cardDefs[cardIdOrName] || Object.values(cardDefs).find(def => def.name === cardIdOrName);
          }).filter(Boolean);

          if (newDeckCards.some(card => card.is_token)) {
            setModalInfo({ isOpen: true, message: getUILoc('MESSAGES.INVALID_CARD_IN_DECK') });
            event.target.value = '';
            return;
          }

          if (newDeckCards.some(card => card.is_enabled === false)) {
            setModalInfo({ isOpen: true, message: getUILoc('MESSAGES.INVALID_CARD_IN_DECK') });
            event.target.value = '';
            return;
          }

          setDeckName(importedDeckData.name);
          setDeck(newDeckCards.sort((a, b) => a.required_scale - b.required_scale));

          const newCustomDecks = {
            ...allDecks,
            [importedDeckData.name]: {
              name: importedDeckData.name,
              cards: importedDeckData.cards,
            }
          };
          saveToStorage('customDecks', newCustomDecks);
          setAllDecks(newCustomDecks);

          setModalInfo({ isOpen: true, message: getUILoc('MESSAGES.IMPORT_SUCCESS', { name: importedDeckData.name }) });
        } else {
          throw new Error(getUILoc('MESSAGES.INVALID_DECK_FORMAT'));
        }
      } catch (error) {
        setModalInfo({ isOpen: true, message: getUILoc('MESSAGES.IMPORT_ERROR', { message: error.message }) });
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  if (!cardDefs) {
    return (
      <div className="deck-builder-screen">
        <div className="deck-builder-container">
          <h1>{getUILoc('SCREENS.DECK_BUILDER')}</h1>
          <p>{getUILoc('MESSAGES.LOAD_DEFS_ERROR')}</p>
          <button onClick={handleExit} className="exit-button">{getUILoc('BUTTONS.EXIT_TO_MENU')}</button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="deck-builder-screen">
      <InfoModal 
        isOpen={modalInfo.isOpen}
        message={modalInfo.message}
        onClose={() => {
            audioManager.playSE('cancel');
            setModalInfo({ isOpen: false, message: '' });
        }}
      />
      <DeckBuilderHelpOverlay 
        isOpen={isHelpVisible}
        onClose={() => {
            audioManager.playSE('cancel');
            setHelpVisible(false);
        }}
        onRestartTutorial={restartTutorial}
      />

      {isTutorialActive && tutorialData && (
          <>
            {tutorialData.targetId && (
                <TutorialHighlight 
                    targetId={tutorialData.targetId} 
                    onTargetClick={nextTutorialStep}
                />
            )}
            <TutorialModal 
                step={{ text: tutorialData.text }}
                onNext={nextTutorialStep}
                isTransparent={!!tutorialData.targetId}
            />
          </>
      )}

      <div className="deck-builder-container">
        <div className="deck-builder-header">
          <div className="header-left">
            <h1>{getUILoc('SCREENS.DECK_BUILDER')}</h1>
            <button id="help-button-trigger" onClick={() => { audioManager.playSE('select'); setHelpVisible(true); }} className="help-button">?</button>
          </div>
          <button onClick={handleExit} className="back-button">{getUILoc('BUTTONS.EXIT_TO_TITLE')}</button>
        </div>
        <div className="deck-builder-layout">
          <div className="card-library-section">
            <div className="library-header">
              <div className="library-title-row">
                <h2>{getUILoc('LABELS.CARDS')}</h2>
                <div className="card-search-container">
                  <input
                    type="text"
                    className="card-search-input"
                    placeholder={getUILoc('LABELS.SEARCH_CARDS')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      className="card-search-clear-btn"
                      onClick={() => setSearchQuery('')}
                      aria-label="Clear search"
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
              <div className="library-controls">
                <div className="sort-controls">
                  <label>{getUILoc('LABELS.SORT')}:</label>
                  <select value={sortBy} onChange={(e) => { audioManager.playSE('select'); setSortBy(e.target.value); }}>
                    <option value="implementation">{getUILoc('LABELS.SORT_IMPLEMENTATION')}</option>
                    <option value="requiredScale">{getUILoc('LABELS.SORT_SCALE')}</option>
                  </select>
                </div>
                <div className="filter-controls">
                  <label>{getUILoc('LABELS.CARD_TYPE')}:</label>
                  <select value={filterType} onChange={(e) => { audioManager.playSE('select'); setFilterType(e.target.value); }}>
                    <option value="all">{getUILoc('LABELS.FILTER_ALL_TYPE')}</option>
                    <option value="1">{getUILoc('CARD_TYPES.1')}</option>
                    <option value="2">{getUILoc('CARD_TYPES.2')}</option>
                    <option value="3">{getUILoc('CARD_TYPES.3')}</option>
                  </select>

                  <label>{getUILoc('LABELS.REQUIRED_SCALE')}:</label>
                  <select value={filterScale} onChange={(e) => { audioManager.playSE('select'); setFilterScale(e.target.value); }}>
                    <option value="all">{getUILoc('LABELS.FILTER_ALL_SCALE')}</option>
                    <option value="0-2">0-2</option>
                    <option value="3-15">3-15</option>
                    <option value="16+">16+</option>
                  </select>
                </div>
              </div>
            </div>
            <div id="card-library-grid" className="deck-builder-card-grid">
              {filteredAndSortedCards.map((card, index) => (
                <div 
                    key={card.id} 
                    className="library-card-hover-zone"
                    onMouseEnter={(e) => handleMouseEnter(card, e)}
                    onMouseLeave={handleCardMouseLeave}
                    style={{ position: 'relative' }}
                >
                    <DeckBuilderLibraryCard
                      id={index === 0 ? "first-library-card" : undefined}
                      card={card}
                      onTap={() => addCardToDeck(card)}
                      onSwipeLeft={() => handleShowCardDetails(card)}
                    />
                    {hoveredCard?.id === card.id && (
                      <div 
                        ref={previewRef}
                        className="preview-tooltip-container"
                        style={{ 
                          position: 'fixed', 
                          left: previewPos.x,
                          top: previewPos.y,
                          zIndex: 2000,
                          pointerEvents: 'auto'
                        }}
                      >
                        <div onWheel={(e) => e.stopPropagation()}>
                          <CardPreview card={hoveredCard} cardDefs={Object.values(cardDefs)} />
                        </div>
                      </div>
                    )}
                </div>
              ))}
            </div>
          </div>

          <div className="deck-building-section">
            <h2 id="deck-size-info">{getUILoc('LABELS.CURRENT_DECK')} ({deck.length}/{deck.length <= MIN_DECK_SIZE ? MIN_DECK_SIZE : MAX_DECK_SIZE})</h2>
            <div className="deck-list">
              {deck.length === 0 ? (
                <div id="empty-deck-placeholder" className="empty-deck-placeholder">
                  <p dangerouslySetInnerHTML={{ __html: getUILoc('MESSAGES.ADD_CARD_FROM_POOL') }}></p>
                </div>
              ) : (
                Object.values(deck.reduce((acc, card) => {
                  if (!acc[card.id]) {
                  acc[card.id] = { card: card, count: 0 };
                }
                acc[card.id].count++;
                return acc;
              }, {})).map(({ card, count }) => (
                <div 
                  key={card.id} 
                  className="deck-card"
                  onClick={() => removeCardFromDeck(card, deck.findIndex(c => c.id === card.id))}
                  title={getUILoc('LABELS.REMOVE_CARD_HINT')}
                >
                <CardIllustration cardId={card.id} isBackground={true} />
                {count > 1 && <div className="deck-card-count">x{count}</div>}
                <span className="card-name-label">{getCardName(card.id)} ({card.required_scale})</span>
                </div>
              ))
              )}
            </div>
            <div className="cost-curve-wrapper">
              <div className="cost-curve-header">
                <h3>{getUILoc('LABELS.SCALE_DISTRIBUTION')}</h3>
                <button onClick={() => { audioManager.playSE('select'); setCostCurveMode(costCurveMode === 'single' ? 'grouped' : 'single'); }}>
                  {getUILoc('LABELS.SWITCH_DISPLAY')} ({costCurveMode === 'single' ? getUILoc('LABELS.STEP_1') : getUILoc('LABELS.STEP_5')})
                </button>
              </div>
              <CostCurveChart data={costCurveData} />
            </div>
            <div className="deck-actions">
              <input
                type="text"
                value={deckName}
                onChange={(e) => setDeckName(e.target.value)}
                onMouseDown={(e) => e.stopPropagation()}
                placeholder={getUILoc('LABELS.DECK_NAME_PLACEHOLDER')}
                className="deck-name-input"
              />
              <button onClick={handleSaveDeck} className="primary-action">{getUILoc('BUTTONS.SAVE')}</button>
              <button onClick={handleExportDeck} className="secondary-action">{getUILoc('BUTTONS.EXPORT')}</button>
              <button onClick={handleImportClick} className="secondary-action">{getUILoc('BUTTONS.IMPORT')}</button>
              <input 
                type="file" 
                ref={fileInputRef} 
                style={{ display: 'none' }} 
                accept=".json" 
                onChange={handleImportDeck} 
              />
            </div>
          </div>
        </div>
      </div>
      {selectedCard && (
        <CardDetail
          card={selectedCard}
          cardDefs={Object.values(cardDefs)}
          onClose={() => {
              audioManager.playSE('cancel');
              setSelectedCard(null);
          }}
        />
      )}
    </div>
  );
};

export default DeckBuilderScreen;
